<script setup>
import { ref } from 'vue';
import router from '@/router';
import { useMutation } from '@tanstack/vue-query';
import { API } from '@/services/index';
import { REGISTER_USER_MUTATION, INVITE_CODE_STATUS_MUTATION } from '@/services/auth/mutations/index.js';
import Cookies from 'js-cookie';
import SocialLoginButtons from '@/components/auth/socialLoginButtons.vue';
import { RouterLink } from 'vue-router';
import { useUserStore } from '@/stores/user.store.js';
import { useAuthStore } from '@/stores/auth.store.js';

const registerForm = ref();
const isFormValid = ref(false);
const email = ref(null);
const password = ref(null);
const firstName = ref(null);
const lastName = ref(null);
const username = ref(null);
const companyName = ref(null);
const phoneNumber = ref(null);
const showPassword = ref(false);
const isLoading = ref(false);
const isLoadingInvite = ref(false);
const newUser = ref(false);
const registerErrors = ref(null);
const route = useRoute();
const inviteData = ref(null);
const token = route.query?.token ?? null;
const loginUrl = ref('/login');

const formRules = {
  // username: (value) => !!value || 'Username is required',
  firstName: (value) => !!value || 'First name is required',
  lastName: (value) => !!value || 'Last name is required',
  // phoneNumber: (value) => !!value || 'Phone number is required',
  phoneRegex: (value) => (value?.length > 9 && /[0-9-]+/.test(value)) || 'Phone number needs to be at least 10 digits.',
  // username: (value) => !!value || 'Username is required',
  email: (value) => !!value || 'Email is required',
  emailRegex: (value) => useUserStore().emailRegex.test(value) || 'Email must be valid',
  password: (value) => !!value || 'Password is required',
  passwordRegex: (value) =>
    useUserStore().passwordRegex.test(value) ||
    'Password must be a minimum of eight characters, at least one uppercase letter, one lowercase letter, one number and one special character'
};

const onSubmitHandle = () => {
  isLoading.value = true;

  const payload = {
    username: username.value,
    first_name: firstName.value,
    last_name: lastName.value,
    email: email.value,
    phone: phoneNumber.value,
    company: companyName.value,
    password: password.value
  };
  registerUserMutation.mutateAsync(payload);
};

const registerUserMutation = useMutation({
  mutationFn: async (payload) => {
    return await API.sendQuery('/graphql', REGISTER_USER_MUTATION, payload);
  },
  onSuccess: (data) => {
    console.log(data);
    if (data.data.data.registerUser) {
      useAuthStore().login(data.data.data.registerUser.token);
      //Cookies.set('_pup_access_token', data.data.data.registerUser.token, {
      //  expires: 7,
      //  sameSite: 'strict',
      //  secure: true
      //});
      localStorage.setItem('_pup_new_user', true);
      localStorage.setItem('_pup_new_user_email', email.value);

      if (companyName.value) {
        localStorage.setItem('_pup_new_account_company_name', JSON.stringify(companyName.value));
      }
      if (data.data.data.registerUser) {
        router.push('/verify' + (token ? '?token=' + token : ''));
      } else {
        registerErrors.value = 'hmmmm';
      }
    }
    if (data.data.errors) {
      console.log('errors', data.data.errors);
      isLoading.value = false;
      registerErrors.value = data.data.errors[0].extensions.debugMessage ?? data.data.errors[0].message;
    }
  },
  onError: (error) => {
    isLoading.value = false;
    console.log('error', error);
  }
});

const inviteCodeStatusMutation = useMutation({
  mutationFn: async (payload) => {
    return await useAuthStore().getInviteData(payload.token);
  },
  onSuccess: () => {
    if(useAuthStore().inviteData)
    {
      inviteData.value = useAuthStore().inviteData;
      isLoadingInvite.value = useAuthStore().isLoadingInvite;

      if (inviteData.value.new_user === false)
      {
        registerErrors.value = 'You already have an account, please login instead.';
        newUser.value = true;
      } 
    }

    if (useAuthStore().errors) {
      console.log(useAuthStore().errors);
    }

    isLoadingInvite.value = false;
  },
  onError: (error) => {
    console.log('error', error);
    isLoadingInvite.value = false;
  }
});

if(token)
{
  isLoadingInvite.value = true;
  inviteData.value = {loading: true};
  loginUrl.value = '/login?token=' + token;
  inviteCodeStatusMutation.mutateAsync({ token: token });
}

</script>

<template>
  <div>
    <h3 class="tw-text-center tw-font-mono tw-text-lg tw-font-bold tw-text-primary-blue">Create an account</h3>
    <div class="tw-mt-5">
      <!--      <div class="tw-mb-3 tw-mt-8">-->
      <!--        <SocialLoginButtons />-->
      <!--      </div>-->
      <!--      <div class="tw-mt-8 tw-flex tw-justify-center">-->
      <!--        <v-divider color="primary-black" class="border-opacity-100" thickness="1"></v-divider>-->
      <!--        <div class="tw-relative tw-top-[-7px] tw-mx-4 tw-text-[12px] tw-font-bold tw-text-primary-blue">OR</div>-->
      <!--        <v-divider color="primary-black" class="border-opacity-100" thickness="1"></v-divider>-->
      <!--      </div>-->
      <p v-if="registerErrors" class="tw-mb-1 tw-text-center tw-text-[0.8rem] tw-text-red-500">
        {{ registerErrors }}
      </p>
      <v-form class="" ref="registerForm" v-model="isFormValid" @submit.prevent="onSubmitHandle">
        <div class="tw-flex tw-w-full tw-flex-col tw-space-y-3">
          <div class="tw-flex tw-w-full tw-flex-col">
            <v-text-field
              v-model="firstName"
              :rules="[formRules.firstName]"
              variant="outlined"
              color="primary-blue"
              placeholder="First Name"
              hide-details="auto"
              density="comfortable"
              :disabled="isLoading"
            >
            </v-text-field>
          </div>
          <div class="tw-flex tw-w-full tw-flex-col">
            <v-text-field
              v-model="lastName"
              :rules="[formRules.lastName]"
              variant="outlined"
              color="primary-blue"
              placeholder="Last Name"
              hide-details="auto"
              density="comfortable"
              :disabled="isLoading"
            >
            </v-text-field>
          </div>
          <div class="tw-flex tw-w-full tw-flex-col">
            <v-text-field
              v-model="email"
              :rules="[formRules.email, formRules.emailRegex]"
              variant="outlined"
              color="primary-blue"
              placeholder="Email Address*"
              hide-details="auto"
              density="comfortable"
              :disabled="isLoading"
            >
            </v-text-field>
          </div>
          <div class="tw-flex tw-w-full tw-flex-col">
            <v-text-field
              v-model="phoneNumber"
              variant="outlined"
              color="primary-blue"
              placeholder="Phone Number"
              hide-details="auto"
              density="comfortable"
              :disabled="isLoading"
            >
            </v-text-field>
          </div>
          <div class="tw-flex tw-w-full tw-flex-col">
            <v-text-field
              v-model="companyName"
              variant="outlined"
              color="primary-blue"
              placeholder="Company Name"
              hide-details="auto"
              density="comfortable"
              :disabled="isLoading"
            >
            </v-text-field>
          </div>
          <div class="tw-flex tw-w-full tw-flex-col">
            <v-text-field
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              :rules="[formRules.password, formRules.passwordRegex]"
              variant="outlined"
              color="primary-blue"
              placeholder="Password*"
              density="comfortable"
              :disabled="isLoading"
            >
              <template v-slot:append-inner>
                <v-icon color="primary-blue" @click="showPassword = !showPassword" :style="password ? 'opacity: 100%' : ''">
                  {{ showPassword ? 'mdi-eye-off' : 'mdi-eye' }}
                </v-icon>
              </template>
            </v-text-field>
          </div>
        </div>
        <div class="tw-mt-3 tw-flex tw-w-full">
          <v-btn
            color="primary-blue"
            size="large"
            rounded="lg"
            block
            flat
            class="tw-font-bold tw-capitalize"
            style="letter-spacing: 0px"
            type="submit"
            :loading="isLoading"
            :disabled="isLoading || !isFormValid || newUser"
          >
            Sign Up For Free
          </v-btn>
        </div>
      </v-form>
      <div class="tw-mt-3 tw-w-full tw-text-center tw-text-[0.813rem] tw-font-semibold tw-text-primary-blue">
        <span class="tw-font-normal">Already have an account?</span>
        <RouterLink class="tw-ml-1 tw-text-[0.813rem] tw-font-bold" :to="loginUrl"> Sign in! </RouterLink>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
